import { Capacitor, registerPlugin } from "@capacitor/core";

/**
 * L'accesso con Google e Apple dentro l'app, sulla strada che Apple raccomanda.
 *
 * IL DIFETTO (Davide dall'iPhone, 09/09/2026): «si apre un browser che non ha i
 * miei login gia' fatti, se vado su Safari trovo i miei account Google
 * loggati». Il browser che si apriva era `SFSafariViewController`, quello di
 * `@capacitor/browser`: da iOS 11 ha un archivio cookie **isolato per ogni
 * app**, quindi non vede nessuno degli account collegati in Safari. Sembra
 * Safari e non lo e'.
 *
 * `ASWebAuthenticationSession` - il plugin `AccessoWeb`, scritto in
 * `ios/App/App/AccessoWebPlugin.swift` - condivide i cookie di Safari,
 * intercetta lo schema di ritorno e si chiude da sola. Restituisce
 * l'indirizzo di ritorno a chi ha chiamato, quindi **il giro non passa piu' da
 * `appUrlOpen`**: la sessione si completa subito, nella stessa funzione.
 *
 * DOVE NON VALE, e non e' una dimenticanza:
 *  - **Android**: la classe e' di iOS. La' le Chrome Custom Tabs la sessione la
 *    condividono davvero e il ritorno per deep link funziona, quindi resta il
 *    giro con `@capacitor/browser` + `RitornoAccessoNativo`.
 *  - **guscio nativo vecchio**: chi ha installato una build precedente non ha
 *    il plugin. `disponibile()` risponde di no e si ripiega, invece di lasciare
 *    il bottone muto.
 *  - **web e PWA**: non c'e' niente da intercettare, il browser resta dov'e'.
 *
 * ATTENZIONE, e non dipende da questo file: la finestra vede lo schema di
 * ritorno solo se e' **Supabase** a mandarcelo. Se
 * `it.datacomtecnologie.ai://auth/callback` non e' nella lista Redirect URLs
 * del progetto, Supabase lo scarta e rimbalza sul sito - e la finestra resta
 * aperta li', esattamente come prima.
 */
type AccessoWebNativo = {
  accedi(opzioni: { url: string; schema: string }): Promise<{
    url?: string;
    annullato?: boolean;
  }>;
};

/**
 * Registrato alla prima chiamata, non al caricamento del modulo.
 *
 * `registerPlugin` al livello del file gira appena qualcuno importa questo
 * modulo - anche nei test, dove `@capacitor/core` e' sostituito e quella
 * funzione non esiste: due file di prova si rompevano solo per averlo
 * importato. Qui dentro gira solo quando serve davvero, cioe' dentro l'app.
 */
let nativo: AccessoWebNativo | null = null;
function plugin(): AccessoWebNativo {
  if (!nativo) nativo = registerPlugin<AccessoWebNativo>("AccessoWeb");
  return nativo;
}

/** Lo schema senza "://": e' cosi' che iOS lo confronta. */
const SCHEMA = "it.datacomtecnologie.ai";

/**
 * Vale la pena provare la finestra su questo telefono?
 *
 * Solo la piattaforma, NIENTE `isPluginAvailable`. Quel controllo sembrava
 * prudente ed era la causa del guasto: Davide, 09/09, dopo la build 4 - «apre
 * ancora un browser che non trova i miei account». Rispondeva di no anche col
 * plugin dentro il pacchetto, e si ripiegava in silenzio sul browser isolato,
 * cioe' esattamente il difetto da chiudere.
 *
 * Il modello giusto ce l'avevamo gia' in casa: `lib/stampa.ts` non chiede il
 * permesso a nessuno, chiama e ripiega se la chiamata viene respinta. Un
 * plugin assente si fa sentire da solo.
 */
export function valeLaPenaProvare(): boolean {
  return Capacitor.getPlatform() === "ios";
}

/**
 * Apre la finestra di accesso e aspetta il ritorno.
 *
 * @returns l'indirizzo di ritorno con il codice, oppure `null` se la persona ha
 *   chiuso la finestra col dito - che non e' un errore e non merita un avviso.
 * @throws se iOS non apre la finestra o il giro fallisce davvero.
 */
export type EsitoAccesso =
  /** Il giro e' andato: c'e' l'indirizzo di ritorno col codice. */
  | { stato: "ok"; url: string }
  /** Finestra chiusa col dito: non e' un errore e non merita un avviso. */
  | { stato: "annullato" }
  /** Il plugin non c'e' o non ha aperto: si ripiega, invece di lasciare il
   *  bottone muto. Peggio che vada, si torna al giro di prima. */
  | { stato: "ripiego"; perche: string };

export async function accediConFinestraSicura(url: string): Promise<EsitoAccesso> {
  try {
    const esito = await plugin().accedi({ url, schema: SCHEMA });
    if (esito.annullato) return { stato: "annullato" };
    if (!esito.url) return { stato: "ripiego", perche: "nessun indirizzo di ritorno" };
    return { stato: "ok", url: esito.url };
  } catch (e) {
    // Guscio senza il plugin, o iOS che non apre: in tutt'e due i casi e'
    // meglio il browser di prima che un bottone che non fa niente.
    return { stato: "ripiego", perche: (e as Error)?.message ?? "plugin non disponibile" };
  }
}

/** Il nome dell'evento con cui la finestra consegna l'indirizzo di ritorno. */
export const EVENTO_RITORNO = "datacom:ritorno-accesso";

/**
 * Consegna l'indirizzo di ritorno a chi sa completare l'accesso.
 *
 * Non lo completa qui: lo scambio del codice e la scelta di dove atterrare
 * stanno in `RitornoAccessoNativo`, che fa la stessa cosa per il ritorno via
 * deep link di Android. Due strade per lo stesso esito divergono sempre, e
 * quando divergono se ne accorge l'installatore, non noi.
 */
export function annunciaRitorno(url: string): void {
  window.dispatchEvent(new CustomEvent(EVENTO_RITORNO, { detail: url }));
}
